import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import type { CampaignLaunchBlockReason } from 'fym-shared/src/music24/campaignLaunch';
import { TypedMemo } from '../../../../../../common/types';
import { Panel } from '../../../../../components/primitives/Panel';
import { platformName } from '../../../../platformMeta';
import { isSocialPlatform } from '../../../../types';
import { launchHeadline, type LaunchFix, type LaunchOutcome, type LaunchSummaryRow } from './launchModel';
import { launchMessages } from './launchMessages';
import type { PostingSplit } from './postingModes';
import { withBrandWord } from '../../../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from '../Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const REASON_MESSAGES: Record<CampaignLaunchBlockReason, MessageDescriptor> = {
  needsAnswer: launchMessages.reasonNeedsAnswer,
  reconnect: launchMessages.reasonReconnect,
  mediaMissing: launchMessages.reasonMediaMissing,
  mediaRefused: launchMessages.reasonMediaRefused,
  monthLimit: launchMessages.reasonMonthLimit,
  channelLimit: launchMessages.reasonChannelLimit,
  timePast: launchMessages.reasonTimePast,
  tiktokWatermark: launchMessages.reasonTikTokWatermark,
  xMonthLimit: launchMessages.reasonXMonthLimit,
};

const FIX_MESSAGES: Record<LaunchFix, MessageDescriptor> = {
  answer: launchMessages.fixAnswer,
  reconnect: launchMessages.fixReconnect,
  openPost: launchMessages.fixOpenPost,
  plans: launchMessages.fixPlans,
};

/** The name of a platform for the copy: the brand name, or the raw value of an unknown platform. */
export const launchPlatformName = (platform: string): string =>
  isSocialPlatform(platform) ? platformName(platform) : platform;

interface RowViewProps {
  row: LaunchSummaryRow;
  isDisabled: boolean;
  onFix: (row: LaunchSummaryRow) => void;
}

/** One post that was not scheduled: its name, the reason in plain words and the one fix. */
const RowViewComponent: React.FC<RowViewProps> = ({ row, isDisabled, onFix }) => {
  const intl = useIntl();
  // The server sentence of a refused file says the rule that broke ("Instagram takes images from 4:5 to 1.91:1").
  const reason =
    row.detail ?? intl.formatMessage(REASON_MESSAGES[row.reason], { name: launchPlatformName(row.platform) });
  const fix = useCallback(() => onFix(row), [onFix, row]);

  return (
    <div className={styles.need} data-testid="launch-row">
      <span className={clsx(styles.needMark, styles.needBad)}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </span>
      <div>
        <b>{row.title}</b>
        <small>{withBrandWord(reason)}</small>
      </div>
      <button type="button" className={styles.link} disabled={isDisabled} onClick={fix}>
        {intl.formatMessage(FIX_MESSAGES[row.fix])}
      </button>
    </div>
  );
};

const RowView = TypedMemo(RowViewComponent);

interface PostsLaunchSummaryProps {
  /** What the last launch did. Null: nothing to show. */
  outcome: LaunchOutcome | null;
  /** One row for each post of `outcome.needsYou`, named by the screen and in its order. */
  rows: LaunchSummaryRow[];
  /** True: the fix buttons are off (a write runs, or the artist is read-only). */
  isDisabled: boolean;
  /** Runs the one fix of a row (Answer, Reconnect, Open the post, See plans). */
  onFix: (row: LaunchSummaryRow) => void;
  onDismiss: () => void;
  /** The headline when every post is scheduled. The default speaks of a campaign. */
  allScheduledTitle?: string;
  /**
   * The scheduled posts split by how they go out (QA finding F02). Null or absent: the old text,
   * which does not say how the posts go out.
   */
  split?: PostingSplit | null;
  /** Opens Connections. With it, a summary of reminders only offers "Connect a channel". */
  onConnectChannels?: () => void;
}

/**
 * What a launch did (owner report 2026-10-10). All scheduled from "Schedule all" of a plan: "Your
 * campaign is scheduled". Else "18 scheduled. 4 need you:" with one row for each post: its name,
 * the reason and one button that fixes it. It stays until the next launch or "Close". It never
 * shows one error for all posts. The plan and the Calendar show this same summary.
 */
const PostsLaunchSummaryComponent: React.FC<PostsLaunchSummaryProps> = ({
  outcome,
  rows,
  isDisabled,
  onFix,
  onDismiss,
  allScheduledTitle,
  split,
  onConnectChannels,
}) => {
  const intl = useIntl();
  const headline = outcome === null ? 'none' : launchHeadline(outcome);
  if (outcome === null || headline === 'none') {
    return null;
  }

  const close = (
    <button type="button" className={styles.link} onClick={onDismiss}>
      {intl.formatMessage(launchMessages.dismiss)}
    </button>
  );

  if (headline === 'allScheduled') {
    const onlyReminders = split !== undefined && split !== null && split.auto === 0;
    let title = allScheduledTitle ?? intl.formatMessage(launchMessages.allScheduledTitle);
    if (onlyReminders) {
      title = intl.formatMessage(launchMessages.allRemindersTitle);
    }
    // Never "go out at their times" when no post goes out by itself (QA finding F02).
    const body =
      split === undefined || split === null
        ? intl.formatMessage(launchMessages.allScheduledBody, { count: outcome.scheduled })
        : intl
            .formatMessage(launchMessages.splitBody, { auto: split.auto, manual: split.manual })
            .replace(/\s{2,}/g, ' ')
            .trim();
    return (
      <Panel as="section" role="status" data-testid="launch-summary">
        <div className={styles.need}>
          <span className={clsx(styles.needMark, styles.launchOk)}>
            <FontAwesomeIcon icon={faCheck} />
          </span>
          <div>
            <b>{title}</b>
            <small>{body}</small>
            {onlyReminders && onConnectChannels !== undefined && (
              <small data-testid="launch-reminders-hint">
                {intl.formatMessage(launchMessages.remindersHint)}{' '}
                <button type="button" className={styles.link} onClick={onConnectChannels}>
                  {intl.formatMessage(launchMessages.connectChannel)}
                </button>
              </small>
            )}
          </div>
          {close}
        </div>
      </Panel>
    );
  }

  return (
    <Panel as="section" role="status" data-testid="launch-summary">
      <div className={styles.launchHead}>
        <b>
          {headline === 'partScheduled'
            ? intl.formatMessage(launchMessages.partTitle, { scheduled: outcome.scheduled, left: rows.length })
            : intl.formatMessage(launchMessages.noneTitle, { left: rows.length })}
        </b>
        {close}
      </div>
      <div className={styles.needs}>
        {rows.map((row) => (
          <RowView key={row.postUuid} row={row} isDisabled={isDisabled} onFix={onFix} />
        ))}
      </div>
    </Panel>
  );
};

export const PostsLaunchSummary = TypedMemo(PostsLaunchSummaryComponent);
